import { useState } from "react";
import { ArrowRight, Phone, Mail, Instagram } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { Reveal } from "../hooks/useScrollReveal";

// FormSubmit AJAX endpoint — sends the submission by e-mail to kontakt@tervalia.cz.
// No backend server is required. First submission from a new address must be
// confirmed once via the activation e-mail FormSubmit sends.
const FORMSUBMIT_ENDPOINT = "https://formsubmit.co/ajax/kontakt@tervalia.cz";

export default function Contact() {
  const { t, locale } = useI18n();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    project_details: "",
    message: "",
    _honey: "", // FormSubmit honeypot — must remain empty for humans
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t.contact.form.required;
    if (!form.phone.trim()) e.phone = t.contact.form.required;
    if (!form.email.trim()) e.email = t.contact.form.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = t.contact.form.invalid_email;
    if (!form.message.trim()) e.message = t.contact.form.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (evt) => {
    evt.preventDefault();
    if (!validate()) return;
    // If the honeypot has any value, silently pretend success and drop.
    if ((form._honey || "").trim() !== "") {
      setStatus("success");
      setForm((f) => ({ ...f, name: "", phone: "", email: "", project_details: "", message: "", _honey: "" }));
      return;
    }
    setStatus("submitting");
    try {
      const payload = {
        Jméno: form.name,
        Telefon: form.phone,
        "E-mail": form.email,
        "Detaily projektu": form.project_details,
        Zpráva: form.message,
        Jazyk: locale,
        _subject: "Nová poptávka z webu Tervalia",
        _template: "table",
        _captcha: "false",
        _honey: form._honey,
      };
      const res = await fetch(FORMSUBMIT_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || (data.success && data.success !== "true" && data.success !== true)) {
        throw new Error(data.message || "FormSubmit error");
      }
      setStatus("success");
      setForm({ name: "", phone: "", email: "", project_details: "", message: "", _honey: "" });
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#292724] text-[#f5f2ec]"
      style={{ marginTop: "-1px" }}
    >
      {/* Organic top edge — a light shape (same colour as the FAQ section) sits on top of the dark section and dips into it, so the dark section actually begins along that curve. No visible horizontal line. */}
      <div
        className="absolute top-0 left-0 right-0 pointer-events-none"
        style={{ height: "60px" }}
        aria-hidden="true"
      >
        <svg
          viewBox="0 0 1440 60"
          preserveAspectRatio="none"
          className="w-full h-full block"
          aria-hidden="true"
        >
          <path
            d="M0,0 L1440,0 L1440,30 C1200,52 980,26 720,40 C460,54 220,28 0,44 Z"
            fill="#f4f0e9"
          />
        </svg>
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 pt-32 md:pt-44 pb-16 md:pb-20">
        <div className="grid md:grid-cols-2 gap-14 md:gap-20">
          {/* Left */}
          <div>
            <Reveal>
              <div
                className="eyebrow mb-6"
                style={{ color: "rgba(245,242,236,0.55)" }}
                data-testid="contact-eyebrow"
              >
                {t.contact.eyebrow}
              </div>
              <h2
                data-testid="contact-title"
                className="font-serif-hero text-[36px] sm:text-[46px] md:text-[54px] leading-[1.08] text-[#f5f2ec]"
              >
                {t.contact.title}
              </h2>
              <p className="mt-6 text-[14.5px] leading-[1.75] text-[#c8c3b7] max-w-[440px]">
                {t.contact.subtitle}
              </p>

              <div className="mt-10 space-y-4">
                <ContactLine
                  Icon={Phone}
                  href="tel:+420702991238"
                  value="+420 702 991 238"
                  testId="contact-phone"
                />
                <ContactLine
                  Icon={Mail}
                  href="mailto:kontakt@tervalia.cz"
                  value="kontakt@tervalia.cz"
                  testId="contact-email"
                />
                <ContactLine
                  Icon={Instagram}
                  href="https://instagram.com/tervalia"
                  value="@Tervalia"
                  testId="contact-instagram"
                  external
                />
              </div>

              <div className="mt-14 text-[10px] tracking-[0.28em] uppercase text-[#8a857b]">
                {t.contact.founder}
              </div>
            </Reveal>
          </div>

          {/* Right — form */}
          <Reveal>
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              noValidate
              className="space-y-6"
            >
              {/* Honeypot — hidden from users, visible to bots. FormSubmit reads `_honey`. */}
              <div className="tv-honeypot" aria-hidden="true">
                <label>
                  Website
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    name="_honey"
                    value={form._honey}
                    onChange={onChange}
                    data-testid="form-honeypot"
                  />
                </label>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <Field
                  label={t.contact.form.name + " *"}
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  error={errors.name}
                  testId="form-name"
                />
                <Field
                  label={t.contact.form.phone + " *"}
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={onChange}
                  error={errors.phone}
                  testId="form-phone"
                />
              </div>
              <Field
                label={t.contact.form.email + " *"}
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                error={errors.email}
                testId="form-email"
              />
              <Field
                label={t.contact.form.details}
                name="project_details"
                value={form.project_details}
                onChange={onChange}
                testId="form-details"
              />
              <Field
                label={t.contact.form.message + " *"}
                name="message"
                as="textarea"
                value={form.message}
                onChange={onChange}
                error={errors.message}
                placeholder={t.contact.form.placeholder}
                testId="form-message"
              />

              <div className="pt-4 flex flex-col sm:flex-row sm:items-center gap-6">
                <button
                  data-testid="form-submit"
                  type="submit"
                  disabled={status === "submitting"}
                  className="tv-btn-dark-cta"
                >
                  {status === "submitting" ? t.contact.form.submitting : t.contact.form.submit}
                  <ArrowRight size={14} strokeWidth={1.5} className="arrow" />
                </button>

                {status === "success" && (
                  <div
                    data-testid="form-success"
                    className="text-[13px] text-[#d4c3a3] font-serif italic"
                  >
                    {t.contact.form.success}
                  </div>
                )}
                {status === "error" && (
                  <div
                    data-testid="form-error"
                    className="text-[13px] text-[#d47461]"
                  >
                    {t.contact.form.error}
                  </div>
                )}
              </div>
            </form>
          </Reveal>
        </div>

        <div className="mt-20 pt-8 border-t border-[#3d3a35] text-[11px] tracking-[0.16em] uppercase text-[#8a857b]">
          {t.contact.footer}
        </div>
      </div>
    </section>
  );
}

function ContactLine({ Icon, href, value, testId, external }) {
  return (
    <a
      href={href}
      data-testid={testId}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="group flex items-center gap-4 text-[#f5f2ec] hover:text-[#b86a55] transition-colors duration-300"
    >
      <Icon size={18} strokeWidth={1.4} className="text-[#b86a55]" />
      <span className="text-[15px]">{value}</span>
    </a>
  );
}

function Field({ label, name, value, onChange, error, testId, type = "text", as, placeholder }) {
  const inputCls = `tv-input ${error ? "tv-input-error" : ""} ${as === "textarea" ? "tv-input-textarea" : ""}`;
  return (
    <label className="block">
      <span className="tv-label">{label}</span>
      {as === "textarea" ? (
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          className={inputCls}
          placeholder={placeholder}
          data-testid={testId}
          rows={4}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          className={inputCls}
          placeholder={placeholder}
          data-testid={testId}
        />
      )}
      {error && (
        <span className="block mt-1 text-[11px] text-[#d47461]" data-testid={`${testId}-error`}>
          {error}
        </span>
      )}
    </label>
  );
}
